import './SalaryGauge.css';
import type { SalaryBalance } from '../types';

interface SalaryGaugeProps {
  balance: SalaryBalance;
  isLoading?: boolean;
}

function formatKRW(amount: number): string {
  if (amount >= 10000) {
    const man = Math.floor(amount / 10000);
    const rest = amount % 10000;
    return rest > 0
      ? `${man.toLocaleString('ko-KR')}만 ${rest.toLocaleString('ko-KR')}원`
      : `${man.toLocaleString('ko-KR')}만원`;
  }
  return `${amount.toLocaleString('ko-KR')}원`;
}

export default function SalaryGauge({ balance, isLoading = false }: SalaryGaugeProps) {
  const pct = Math.min(100, Math.max(0, balance.percentage));
  const isWarning = pct >= 80;
  const isDanger = pct >= 95;

  const gaugeColor = isDanger
    ? 'var(--color-danger)'
    : isWarning
    ? 'var(--color-warning)'
    : 'var(--color-primary)';

  // 원형 게이지 SVG 계산
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - pct / 100);

  return (
    <div className={`salary-gauge card ${isDanger ? 'danger' : isWarning ? 'warning' : ''}`}>
      <div className="salary-gauge__header">
        <h3 className="salary-gauge__title">이번 달 급여 사용 현황</h3>
        <span className="salary-gauge__reset">
          리셋 예정: {balance.resetDate}
        </span>
      </div>

      {isLoading ? (
        <div className="salary-gauge__skeleton" />
      ) : (
        <div className="salary-gauge__body">
          {/* 원형 게이지 */}
          <div className="salary-gauge__circle-wrap">
            <svg className="salary-gauge__circle-svg" viewBox="0 0 120 120">
              {/* 배경 트랙 */}
              <circle
                cx="60"
                cy="60"
                r={radius}
                fill="none"
                stroke="var(--color-border)"
                strokeWidth="10"
              />
              {/* 진행 바 */}
              <circle
                cx="60"
                cy="60"
                r={radius}
                fill="none"
                stroke={gaugeColor}
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                transform="rotate(-90 60 60)"
                style={{ transition: 'stroke-dashoffset 0.6s ease' }}
              />
            </svg>
            <div className="salary-gauge__circle-center">
              <span
                className="salary-gauge__pct"
                style={{ color: gaugeColor }}
              >
                {pct.toFixed(0)}%
              </span>
              <span className="salary-gauge__pct-label">사용</span>
            </div>
          </div>

          {/* 상세 수치 */}
          <div className="salary-gauge__details">
            <div className="salary-gauge__detail-item">
              <span className="salary-gauge__detail-label">총 급여</span>
              <span className="salary-gauge__detail-value">
                {formatKRW(balance.total)}
              </span>
            </div>
            <div className="salary-gauge__detail-item">
              <span className="salary-gauge__detail-label">사용한 급여</span>
              <span
                className="salary-gauge__detail-value"
                style={{ color: gaugeColor }}
              >
                {formatKRW(balance.used)}
              </span>
            </div>
            <div className="salary-gauge__detail-item highlight">
              <span className="salary-gauge__detail-label">남은 급여</span>
              <span className="salary-gauge__detail-value large">
                {formatKRW(balance.current)}
              </span>
            </div>

            {/* 막대 게이지 */}
            <div className="salary-gauge__bar-wrap">
              <div className="salary-gauge__bar-track">
                <div
                  className="salary-gauge__bar-fill"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: gaugeColor,
                  }}
                />
              </div>
            </div>

            {isDanger && (
              <div className="alert alert-error salary-gauge__alert">
                급여 잔액이 거의 소진되었습니다. 추가 예산을 배정해주세요.
              </div>
            )}
            {isWarning && !isDanger && (
              <div className="alert alert-warning salary-gauge__alert">
                급여 잔액이 20% 미만입니다.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
